import { Group, Stack, Tooltip, createStyles } from '@mantine/core';
import { getHotkeyHandler } from '@mantine/hooks';
import { KeyboardShortcut } from '@repo/common/components/KeyboardShortcut/KeyboardShortcut';
import { Button, IconButton } from '@repo/foundations';
import { MOD_KEY } from '@repo/secoda-editor/utils/keyboard';
import { removeTrailingWhitespaces } from '@repo/secoda-editor/utils/removeTrailingWhitespaces';
import { useBoolean } from 'ahooks';
import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import { RichEditor } from '../../../components/RichEditor';
import { FORM_SHORTCUTS, GLOBAL_SHORTCUTS } from '../../../constants/shortcuts';
import { useForceReRender } from '../../../utils/hook/useForceReRender';
import { useCommentStoreContext } from '../hooks';

const useStyles = createStyles((theme) => ({
	commentForm: {
		position: 'relative',
		backgroundColor: theme.other.getColor('surface/app/default'),
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
		borderRadius: theme.radius.sm,
		fontSize: theme.fontSizes.sm,

		// Artificial padding to create another line in the editor "box"
		// The actions are positioned absolutely at the bottom right
		paddingBottom: theme.other.space[8],
		marginBottom: theme.spacing['2xs'],
	},
	editorContainer: {
		position: 'relative',
		overflowY: 'scroll',
		'::-webkit-scrollbar': {
			display: 'none',
		},
		paddingTop: theme.spacing['2xs'],
		paddingLeft: theme.spacing['2xs'],
	},
	actionGroup: {
		position: 'absolute',
		right: theme.spacing['2xs'],
		bottom: theme.spacing['2xs'],
	},
	actionGroupBackground: {
		backgroundColor: theme.other.getColor('fill/brand/default'),
		'&:disabled': {
			backgroundColor: theme.other.getColor('fill/brand/disabled'),
		},
		'&:hover': {
			backgroundColor: theme.other.getColor('fill/brand/default'),
		},
	},
}));

type ICommentFormProps = {
	dataTestId?: string;
	autoFocus?: boolean;
	isEditExisting?: boolean;
	placeholder?: string;
	definition?: string;
	onSubmit: (commentDefinition: string) => void;
	onCancel: () => void;
	readOnly?: boolean;
	persistEditedValue?: boolean;
};

function CommentForm({
	dataTestId,
	autoFocus = false,
	isEditExisting = false,
	placeholder = 'Leave a comment...',
	definition = '',
	readOnly = false,
	persistEditedValue = false,
	onSubmit,
	onCancel,
}: ICommentFormProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [value, setValue] = useState<string>(definition);
	const [valid, setValid] = useBoolean(false);
	const { uploadingEditorID, setUploadingEditorID } = useCommentStoreContext();

	// Disable form to avoid multiple submission
	const [enabled, setEnabled] = useState<boolean>(true);
	const { key: richEditorKey, forceUpdate } = useForceReRender();
	const { classes, cx } = useStyles();

	const handleCommentChange = (newValue: string | undefined) => {
		if (newValue && removeTrailingWhitespaces(newValue).length > 0) {
			setValue(newValue);
			setValid.setTrue();
		} else {
			setValid.setFalse();
		}
	};

	const handleSubmitComment = async () => {
		if (
			removeTrailingWhitespaces(value) === '' ||
			!enabled ||
			uploadingEditorID === richEditorKey
		) {
			return;
		}
		setEnabled(false);
		setUploadingEditorID(richEditorKey);
		await onSubmit(value);
		if (!persistEditedValue) {
			setValue('');
			setValid.setFalse();
		}
		forceUpdate();
		setEnabled(true);
	};

	const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();
		onCancel();
	};

	const actions = isEditExisting ? (
		<Group className={classes.actionGroup} spacing="xs">
			<Tooltip label={<KeyboardShortcut keys={['Esc']} />}>
				<Button size="sm" onClick={handleCancel}>
					Cancel
				</Button>
			</Tooltip>
			<Tooltip label={<KeyboardShortcut keys={[MOD_KEY, 'Enter']} />}>
				<Button
					data-testid={`${dataTestId}-submit`}
					variant="primary"
					size="sm"
					disabled={!valid}
					onClick={handleSubmitComment}
					loading={richEditorKey === uploadingEditorID}
				>
					Save
				</Button>
			</Tooltip>
		</Group>
	) : (
		<Tooltip label={<KeyboardShortcut keys={[MOD_KEY, 'Enter']} />}>
			<IconButton
				aria-label="Submit comment"
				data-testid={`${dataTestId}-submit`}
				className={`${classes.actionGroup} ${classes.actionGroupBackground}`}
				disabled={!valid}
				loading={richEditorKey === uploadingEditorID}
				size="sm"
				onClick={handleSubmitComment}
				iconName="arrowUp"
				variant="primary"
			/>
		</Tooltip>
	);

	return (
		<Stack
			className={cx({ [classes.commentForm]: !readOnly })}
			onKeyDown={getHotkeyHandler([
				[FORM_SHORTCUTS.SUBMIT_WITH_MOD[0], handleSubmitComment],
				[GLOBAL_SHORTCUTS.ESCAPE[0], onCancel],
			])}
		>
			<Stack className={cx({ [classes.editorContainer]: !readOnly })} ref={ref}>
				<RichEditor
					dataTestId={dataTestId}
					key={richEditorKey}
					autoFocus={autoFocus}
					emptyLinePlaceholder={placeholder}
					readOnly={readOnly}
					onChangeCallback={handleCommentChange}
					initialValue={value}
					disableTopGap
					limited
				/>
			</Stack>
			{!readOnly && actions}
		</Stack>
	);
}

const CommentFormObserver = observer(CommentForm);
export default CommentFormObserver;
